import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Label,
  RadioGroup,
  RadioGroupItem,
  InputField,
} from "@jem-open/jem-ui";
import { CircleOff } from "lucide-react";
import { AttendanceException } from "../services/model/AttendanceException";
import {
  getExceptionDetailsSummary,
  getExceptionShiftLabel,
  getShiftTimeRange,
} from "../utils/exceptionDisplayUtils";

interface NoDataActionModalProps {
  exception: AttendanceException;
  open: boolean;
  onClose: () => void;
  onResolve: (id: string, action: string, reason?: string) => void;
}

const NoDataActionModal: React.FC<NoDataActionModalProps> = ({
  exception,
  open,
  onClose,
  onResolve,
}) => {
  const [action, setAction] = useState<string>("");
  const [clockTime, setClockTime] = useState<string>("");

  useEffect(() => {
    if (open) {
      setAction("");
      setClockTime("");
    }
  }, [exception.id, open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-xs">
            <CircleOff size={18} className="text-gray-500" />
            No Data — Resolve Status
          </DialogTitle>
          <DialogDescription>
            No clock event recorded for {exception.employee_name} after the late
            trigger.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-md py-sm">
          <div className="rounded-lg border border-greyscale-border p-sm space-y-xxs">
            <p className="text-sm">
              <span className="font-semibold">Employee:</span>{" "}
              {exception.employee_name}
            </p>
            <p className="text-sm">
              <span className="font-semibold">Site:</span>{" "}
              {exception.location_name} — {exception.region_name}
            </p>
            <p className="text-sm">
              <span className="font-semibold">Shift:</span>{" "}
              {getExceptionShiftLabel(exception)} (
              {getShiftTimeRange(exception.shift_start, exception.shift_end)})
            </p>
            <p className="text-sm">
              <span className="font-semibold">Late trigger at:</span>{" "}
              {exception.timestamp}
            </p>
            <p className="text-sm text-greyscale-text-caption">
              {getExceptionDetailsSummary(exception)}
            </p>
          </div>

          <div className="space-y-xs">
            <Label className="text-sm font-semibold">Resolve as</Label>
            <RadioGroup value={action} onValueChange={setAction}>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="no_show" id="nodata-noshow" />
                <Label htmlFor="nodata-noshow" className="text-sm">
                  No Show — employee did not arrive
                </Label>
              </div>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="replaced" id="nodata-replaced" />
                <Label htmlFor="nodata-replaced" className="text-sm">
                  Replaced — a replacement was assigned
                </Label>
              </div>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="clock_in" id="nodata-clock-in" />
                <Label htmlFor="nodata-clock-in" className="text-sm">
                  Clock In — employee clocked in manually
                </Label>
              </div>
            </RadioGroup>
          </div>

          {action === "clock_in" && (
            <div className="space-y-xxs">
              <Label
                htmlFor="nodata-clock-time"
                className="text-sm font-semibold"
              >
                Clock-in time
              </Label>
              <InputField
                id="nodata-clock-time"
                type="time"
                value={clockTime}
                onChange={(e) => setClockTime(e.target.value)}
              />
              <p className="text-xs text-greyscale-text-caption">
                Enter the time the employee actually started — a clock-in will
                be created at this time.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            disabled={!action || (action === "clock_in" && !clockTime.trim())}
            onClick={() => {
              onResolve(
                exception.id,
                action,
                action === "clock_in" ? clockTime : undefined
              );
              onClose();
            }}
          >
            Resolve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NoDataActionModal;
